// A market — a grand 3x3 trading hall where players buy and sell. A tall
// enclosed emporium under a gabled terracotta roof with a wide awning-shaded
// entrance, a banner pole flying a pennant, gold coin accents over the door, and
// goods stalls out front. Ported 1:1 from age-of-mcp's market.ts: the shared
// foundation/scaffold/crate/barrel come from the library, the hall body is a
// local helper, and the four build stages are transcribed from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, crate, barrel, type Part } from "../../lib";

const WALL = "#cdab74"; // warm sandstone
const ROOF = "#9a4f33"; // terracotta
const TRIM = "#efe6cf"; // cream trim
const AWNING = "#b23a34"; // deep red awning
const POST = "#7a5a36"; // timber posts
const COIN = "#e8c14a"; // gold coin
const DOOR = "#5a3c24";
const BANNER = "#c23b54"; // pennant
const STONE = "#6a6a6a";

const BASE = 0.14;
const HALLZ = -0.35; // the hall sits toward the back of the 3x3 plot
const HALL_W = 2.0;
const HALL_D = 1.5;

// The enclosed hall body, `h` tiles of wall, at the back of the plot.
function hall(h: number): Part {
  return { geometry: new THREE.BoxGeometry(HALL_W, h, HALL_D), color: WALL, position: [0, BASE + h / 2, HALLZ] };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(2.94)];
  if (s === 1) return [foundationSlab(2.94), hall(0.5), ...scaffold(HALL_W, 0.9)];
  if (s === 2) return [foundationSlab(2.94), hall(1.1), ...scaffold(HALL_W, 1.6)];

  const wallH = 1.3, top = BASE + wallH, rise = 0.7, halfD = HALL_D / 2 + 0.16;
  const slope = Math.hypot(halfD, rise), ang = Math.atan2(rise, halfD);
  const front = HALLZ + HALL_D / 2; // hall's front (entrance) face
  return [
    { geometry: new THREE.BoxGeometry(2.94, BASE, 2.94), color: STONE, position: [0, BASE / 2, 0] },
    hall(wallH),
    // Gabled terracotta roof.
    { geometry: new THREE.BoxGeometry(HALL_W + 0.2, 0.1, slope), color: ROOF, position: [0, top + rise / 2, HALLZ + halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(HALL_W + 0.2, 0.1, slope), color: ROOF, position: [0, top + rise / 2, HALLZ - halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(HALL_W + 0.26, 0.1, 0.1), color: TRIM, position: [0, top + rise, HALLZ] },
    // Wide entrance: a dark doorway with a cream frame and gold coin emblem above.
    { geometry: new THREE.BoxGeometry(0.7, 0.9, 0.06), color: DOOR, position: [0, BASE + 0.45, front + 0.02] },
    { geometry: new THREE.BoxGeometry(0.84, 0.08, 0.08), color: TRIM, position: [0, BASE + 0.92, front + 0.03] },
    { geometry: new THREE.CylinderGeometry(0.13, 0.13, 0.04, 14), color: COIN, position: [0, top + 0.18, front + 0.02], rotation: [Math.PI / 2, 0, 0] },
    // A red awning over the entrance, on two posts.
    { geometry: new THREE.BoxGeometry(1.1, 0.05, 0.5), color: AWNING, position: [0, BASE + 1.0, front + 0.34], rotation: [-0.32, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.9, 6), color: POST, position: [-0.5, BASE + 0.45, front + 0.5] },
    { geometry: new THREE.CylinderGeometry(0.04, 0.04, 0.9, 6), color: POST, position: [0.5, BASE + 0.45, front + 0.5] },
    // Banner pole flying a pennant, on the back corner.
    { geometry: new THREE.CylinderGeometry(0.035, 0.035, 1.4, 6), color: POST, position: [-0.85, BASE + 0.7 + wallH * 0.0, HALLZ - 0.55] },
    { geometry: new THREE.BoxGeometry(0.04, 0.3, 0.42), color: BANNER, position: [-0.85, BASE + 1.55, HALLZ - 0.34] },
    // Goods stalls out front: a couple of crates and a barrel of wares.
    crate(-0.7, 0.9, 0.34),
    crate(-0.35, 0.95, 0.28),
    crate(0.7, 0.9, 0.32),
    barrel(0.35, 0.95, BASE, 0.3),
  ];
}

export const market = defineModel({
  id: "market",
  tiles: { w: 3, h: 3 },
  batchId: "standard",
  name: "Market",
  tags: ["building", "market", "low-poly", "age-of-mcp", "classic"],
  summary: "Low-poly market.",
  description: "A grand trading hall where players buy and sell goods.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
